<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, Select, Textarea } from '@drama/ui'
import { isNovelCharacterCardState } from '@drama/contracts/novel'
import NovelImageField from '../NovelImageField.vue'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import NovelIssueList from '../NovelIssueList.vue'
import NovelCharacterCues from './NovelCharacterCues.vue'
import NovelPositionsSection from './NovelPositionsSection.vue'
import { dramaPresetStateReferences } from '@drama/novel-authoring'
import { assetTag, uniqueId, newCharacter } from '@drama/novel-authoring'
import { pendingSupplementalEntries, supplementalEntryIssues } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import { useEntitySelection } from './entity-selection.js'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, required: true },
  save_states: { type: Object, default: () => ({}) },
  save_disabled: { type: Boolean, default: false },
  guided: { type: Boolean, default: false },
  focus_target: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'blocked', 'save-character', 'go'])
const { t } = useI18n()
const { bind, invalid, mutate, notes, removeAt, removeEntity, write, error } = useSectionModel(props, emit)
const people = computed(() => props.modelValue.characters ?? [])
const pending_entries = computed(() => pendingSupplementalEntries(props.modelValue)
  .filter(entry => entry.group_id === 'character_details'))
function pendingIssues(index) {
  return supplementalEntryIssues(props.modelValue, pending_entries.value
    .filter(entry => entry.path.startsWith(`characters.${index}.`)), t)
}
const { selected_id } = useEntitySelection(props, 'characters')
const character_options = computed(() => people.value.map((person, index) => ({
  value: person.id,
  label: [person.name || `${t('novelStudio.fields.newCharacter')} ${index + 1}`,
    isPlayer(person) ? t('novelStudio.fields.player') : ''].filter(Boolean).join(' · '),
})))

function isPlayer(person) {
  return person.id === props.modelValue.player_id
}

/** 帮助、占位与示例按玩家 / NPC 取不同文案（S-8，迁自 Demo 编辑器）。 */
function roleText(person, key) {
  return t(`novelStudio.fields.${key}${isPlayer(person) ? 'Player' : 'Npc'}`)
}

/** 新人物连同起始位置一起写入，放在第一个地点（Demo 的 add_character）。 */
function addCharacter() {
  mutate(next => {
    next.characters ??= []
    const { person, location_id } = newCharacter(next, t('novelStudio.fields.newCharacter'))
    next.characters.push(person)
    next.initial_state ??= {}
    next.initial_state.positions ??= {}
    next.initial_state.positions[person.id] = location_id
    selected_id.value = person.id
  })
}

/** 人物主图是可选键：清空即删键，避免空串过不了严格校验。 */
function setPortrait(index, url) {
  const person = { ...people.value[index] }
  const references = (person.performance_states ?? []).filter(isNovelCharacterCardState)
    .flatMap(state => dramaPresetStateReferences(props.modelValue, index, state.id))
  if (!url && references.length) {
    emit('blocked', { name: person.name || person.id, references })
    return
  }
  if (url) person.image = url
  else delete person.image
  write(['characters', index], person)
}

function setStateImage(index, state_index, image) {
  const state = { ...people.value[index].performance_states[state_index], image }
  delete state.image_source
  write(['characters', index, 'performance_states', state_index], state)
}

function removableState(person, state) {
  return !isNovelCharacterCardState(state) || person.performance_states.some(other => other !== state && other.image?.trim())
}

function addState(index) {
  const states = people.value[index].performance_states ?? []
  write(['characters', index, 'performance_states'], [...states, { id: uniqueId('state', states), image: '', description: '' }])
}

function removeState(index, state_index) {
  const state = people.value[index].performance_states[state_index]
  const references = dramaPresetStateReferences(props.modelValue, index, state.id)
  if (references.length) {
    emit('blocked', { name: `${people.value[index].name || people.value[index].id} · ${state.id}`, references })
    return
  }
  removeAt(['characters', index, 'performance_states', state_index])
}

function addExample(index) {
  write(['characters', index, 'style_examples'], [...(people.value[index].style_examples ?? []), { situation: '', response: '' }])
}
</script>

<template>
  <section
    class="novel-entities"
    :class="{ 'novel-entity-picker': guided }"
    data-testid="novel-characters-section"
  >
    <header class="novel-entities__header">
      <div class="novel-entities__heading">
        <h3 v-if="guided">
          {{ t('novelStudio.sections.characters') }}
        </h3>
        <p>{{ t('novelStudio.fields.count', { count: people.length }) }}</p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-character"
        @click="addCharacter"
      >
        {{ t('novelStudio.fields.addCharacter') }}
      </Button>
    </header>
    <Select
      v-if="guided && people.length"
      v-model="selected_id"
      :options="character_options"
      :aria-label="t('novelAuthoring.selectCharacter')"
      :disabled="save_disabled"
      block
      data-testid="novel-character-select"
    />
    <NovelPositionsSection
      v-if="guided && people.length"
      :model-value="modelValue"
      :issues="issues"
      :character_id="selected_id"
      @update:model-value="emit('update:modelValue', $event)"
      @blocked="emit('blocked', $event)"
    />
    <p
      v-if="!guided"
      class="novel-entities__hint"
    >
      {{ t('novelStudio.fields.referenceHelp') }}
    </p>
    <p
      v-if="!people.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.characters') }}
    </p>
    <article
      v-for="(person, index) in people"
      v-show="!guided || person.id === selected_id"
      :key="`${person.id}-${index}`"
      class="novel-entity"
      :class="{ 'has-error': invalid(['characters', index]) }"
      :data-path="`characters.${index}`"
      tabindex="-1"
      :data-testid="`novel-character-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3>{{ person.name || person.id }}</h3>
          <code v-if="!guided">{{ person.id }}</code>
          <span
            v-if="isPlayer(person)"
            class="novel-entity__badge"
          >{{ t('novelStudio.fields.player') }}</span>
        </div>
        <Button
          v-if="!isPlayer(person)"
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: person.name || person.id })"
          :data-testid="`novel-character-${index}-remove`"
          @click="removeEntity('characters', index, person.name || person.id)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="notes(['characters', index])" />
      <NovelImageField
        :model-value="person.image || ''"
        :label="t('novelStudio.fields.image')"
        :tag="assetTag('state_image', person.id)"
        :upload="upload"
        :alt="person.name || person.id"
        :invalid="invalid(['characters', index, 'image'])"
        :data-path="`characters.${index}.image`"
        @update:model-value="setPortrait(index, $event)"
      />
      <div class="novel-entity__grid">
        <FormField
          :error="error(['characters', index, 'name'])"
          required
          :label="t('novelStudio.fields.name')"
        >
          <Input
            v-bind="bind(['characters', index, 'name'])"
            :data-testid="`novel-character-${index}-name`"
          />
        </FormField>
        <FormField
          :error="error(['characters', index, 'public_profile'])"
          required
          class="is-wide"
          :label="t('novelStudio.fields.publicProfile')"
        >
          <Textarea
            v-bind="bind(['characters', index, 'public_profile'])"
            :rows="3"
          />
        </FormField>
      </div>
      <details
        class="novel-character-advanced"
        :open="!guided"
      >
        <summary>
          {{ t('novelAuthoring.advancedCharacter') }}
          <span
            v-if="guided && pendingIssues(index).length"
            class="novel-character-pending"
          >
            {{ t('novelAuthoring.pendingCount', { count: pendingIssues(index).length }) }}
          </span>
        </summary>
        <p
          v-if="guided"
          class="novel-entities__hint"
        >
          {{ t('novelAuthoring.supplementalHint') }}
        </p>
        <NovelIssueList
          v-if="guided"
          :issues="pendingIssues(index)"
          summary_key="novelAuthoring.pendingCount"
          @go="emit('go', $event)"
        />
        <div class="novel-entity__grid">
          <FormField :label="t('novelStudio.fields.identity')">
            <Input v-bind="bind(['characters', index, 'profile', 'identity'])" />
          </FormField>
          <FormField :label="t('novelStudio.fields.appearance')">
            <Textarea
              v-bind="bind(['characters', index, 'profile', 'appearance'])"
              :rows="2"
            />
          </FormField>
          <FormField :label="t('novelStudio.fields.speechStyle')">
            <Textarea
              v-bind="bind(['characters', index, 'profile', 'speech_style'])"
              :rows="2"
            />
          </FormField>
          <FormField
            class="is-wide"
            :label="t('novelStudio.fields.personality')"
            :hint="roleText(person, 'personalityHelp')"
          >
            <Textarea
              v-bind="bind(['characters', index, 'profile', 'personality'])"
              :rows="5"
              :placeholder="roleText(person, 'personalityPlaceholder')"
              :data-testid="`novel-character-${index}-personality`"
            />
          </FormField>
          <div class="novel-entity__help is-wide">
            <p>{{ t('novelStudio.fields.personalityScope') }}</p>
            <p>{{ t('novelStudio.fields.personalityUse') }}</p>
            <details>
              <summary>{{ t('novelStudio.fields.personalityExampleLabel') }}</summary>
              <p>{{ roleText(person, 'personalityExample') }}</p>
            </details>
          </div>
          <FormField
            class="is-wide"
            :label="t('novelStudio.fields.initialGoal')"
            :hint="roleText(person, 'goalHelp')"
          >
            <Textarea
              v-bind="bind(['characters', index, 'initial_goal'])"
              :rows="2"
              :placeholder="roleText(person, 'goalPlaceholder')"
            />
          </FormField>
        </div>
        <section
          class="novel-entity__nested"
          :data-path="`characters.${index}.performance_states`"
        >
          <div class="novel-entity__nested-header">
            <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.performanceStates') }) }}</h4>
            <Button
              variant="secondary"
              size="sm"
              :data-testid="`novel-character-${index}-add-state`"
              @click="addState(index)"
            >
              {{ t('novelStudio.fields.addState') }}
            </Button>
          </div>
          <div
            v-for="(state, stateIndex) in person.performance_states || []"
            :key="`${state.id}-${stateIndex}`"
            class="novel-entity__sub"
            :data-testid="`novel-character-${index}-state-${state.id}`"
            :class="{ 'has-error': invalid(['characters', index, 'performance_states', stateIndex]) }"
          >
            <div class="novel-entity__sub-header">
              <span v-if="isNovelCharacterCardState(state)">{{ t('novelStudio.fields.cardState') }}</span>
              <code v-else>{{ state.id }}</code>
              <Button
                v-if="removableState(person, state)"
                variant="secondary"
                size="sm"
                :aria-label="t('novelStudio.fields.removeNamed', { name: `${person.name || person.id} · ${isNovelCharacterCardState(state) ? t('novelStudio.fields.cardState') : state.id}` })"
                @click="removeState(index, stateIndex)"
              >
                {{ t('novelStudio.fields.remove') }}
              </Button>
            </div>
            <p
              v-if="isNovelCharacterCardState(state)"
              class="novel-entities__hint"
            >
              {{ t('novelStudio.fields.cardStateHelp') }}
            </p>
            <NovelImageField
              required
              :clearable="!isNovelCharacterCardState(state)"
              :model-value="state.image || ''"
              :label="t('novelStudio.fields.image')"
              :tag="assetTag('state_image', person.id, state.id)"
              :upload="upload"
              :alt="isNovelCharacterCardState(state) ? t('novelStudio.fields.cardState') : state.description || `${person.name || person.id} · ${state.id}`"
              :invalid="invalid(['characters', index, 'performance_states', stateIndex, 'image'])"
              :data-path="`characters.${index}.performance_states.${stateIndex}.image`"
              @update:model-value="setStateImage(index, stateIndex, $event)"
            />
            <FormField
              :error="error(['characters', index, 'performance_states', stateIndex, 'description'])"
              :required="!isNovelCharacterCardState(state)"
              :label="t('novelStudio.fields.stateDescription')"
            >
              <Textarea
                v-bind="bind(['characters', index, 'performance_states', stateIndex, 'description'])"
                :rows="2"
                :placeholder="isNovelCharacterCardState(state) ? t('novelStudio.fields.cardStateDescription') : ''"
                maxlength="240"
              />
            </FormField>
          </div>
        </section>
        <NovelCharacterCues
          :model-value="modelValue"
          :issues="issues"
          :index="index"
          @update:model-value="emit('update:modelValue', $event)"
        />
        <section
          class="novel-entity__nested"
          :data-path="`characters.${index}.style_examples`"
        >
          <div class="novel-entity__nested-header">
            <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.styleExamples') }) }}</h4>
            <Button
              variant="secondary"
              size="sm"
              @click="addExample(index)"
            >
              {{ t('novelStudio.fields.addExample') }}
            </Button>
          </div>
          <div
            v-for="(example, exampleIndex) in person.style_examples || []"
            :key="exampleIndex"
            class="novel-entity__sub"
          >
            <FormField
              :error="error(['characters', index, 'style_examples', exampleIndex, 'situation'])"
              required
              :label="t('novelStudio.fields.situation')"
            >
              <Input v-bind="bind(['characters', index, 'style_examples', exampleIndex, 'situation'])" />
            </FormField>
            <FormField
              :error="error(['characters', index, 'style_examples', exampleIndex, 'response'])"
              required
              :label="t('novelStudio.fields.response')"
            >
              <Textarea
                v-bind="bind(['characters', index, 'style_examples', exampleIndex, 'response'])"
                :rows="2"
              />
            </FormField>
            <div>
              <Button
                variant="secondary"
                size="sm"
                :aria-label="t('novelStudio.fields.removeNamed', { name: example.situation || `${t('novelStudio.fields.styleExamples')} #${exampleIndex + 1}` })"
                @click="removeAt(['characters', index, 'style_examples', exampleIndex])"
              >
                {{ t('novelStudio.fields.remove') }}
              </Button>
            </div>
          </div>
        </section>
      </details>
      <footer class="novel-character-save">
        <p
          role="status"
          :data-testid="`novel-character-${index}-state`"
        >
          {{ t(`novelStudio.characters.${save_states[person.id]?.saving ? 'saving' : save_states[person.id]?.dirty ? 'unsaved' : 'saved'}`) }}
        </p>
        <Button
          :disabled="save_disabled || !save_states[person.id]?.dirty"
          :loading="Boolean(save_states[person.id]?.saving)"
          :aria-label="t('novelStudio.characters.saveNamed', { name: person.name || person.id })"
          :data-testid="`novel-character-${index}-save`"
          @click="emit('save-character', person.id)"
        >
          {{ t('novelStudio.characters.save') }}
        </Button>
      </footer>
    </article>
  </section>
</template>

<style scoped>
.novel-character-save {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  padding-top: var(--drama-space-lg);
  border-top: 1px solid var(--drama-color-border);
}

.novel-character-save p {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
.novel-character-pending { margin-inline-start: var(--drama-space-sm); color: var(--drama-color-danger); font-size: var(--drama-font-size-sm); }
</style>
